import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import UserAvatar from './UserAvatar';
import { Presentation, Crown, Check, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Assign Presenter Dialog - Allows facilitator to manually assign presenter
 */
const AssignPresenterDialog = ({
  open,
  onOpenChange,
  meetingId,
  participants = [],
  currentPresenterId,
  onPresenterChange
}) => {
  const [selectedUserId, setSelectedUserId] = useState(currentPresenterId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleAssign = async () => {
    if (!selectedUserId) {
      toast.error('Please select a presenter');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/eos/meetings/${meetingId}/assign-presenter`,
        { presenter_user_id: selectedUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const selectedParticipant = participants.find(p => p.user_id === selectedUserId);
      onPresenterChange?.(selectedUserId);
      onOpenChange(false);
      toast.success(`${selectedParticipant?.display_name || 'User'} is now the presenter`);
    } catch (error) {
      console.error('Error assigning presenter:', error);
      toast.error('Failed to assign presenter');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Presentation className="h-5 w-5 text-orange-500" />
            Assign Presenter
          </DialogTitle>
          <DialogDescription>
            Select a participant to make them the current presenter. This overrides the automatic flow.
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[400px] pr-2">
          <div className="space-y-1">
            {participants.map(participant => {
              const isSelected = participant.user_id === selectedUserId;
              const isCurrentPresenter = participant.user_id === currentPresenterId;
              
              return (
                <button
                  key={participant.user_id}
                  onClick={() => setSelectedUserId(participant.user_id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                    isSelected
                      ? 'bg-orange-500/20 border border-orange-500'
                      : 'hover:bg-muted border border-transparent'
                  }`}
                >
                  <UserAvatar user={participant} size="md" />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">
                        {participant.display_name || participant.email?.split('@')[0]}
                      </span>
                      {participant.role === 'facilitator' && (
                        <Badge className="bg-purple-600 text-[10px] py-0">
                          <Crown className="h-2.5 w-2.5" />
                        </Badge>
                      )}
                      {isCurrentPresenter && (
                        <Badge className="bg-orange-500 text-[10px] py-0">
                          PRESENTER
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {participant.email}
                    </span>
                  </div>
                  
                  {isSelected && (
                    <Check className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </ScrollArea>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedUserId || isSubmitting}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {isSubmitting ? 'Assigning...' : 'Set as Presenter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignPresenterDialog;
